import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Send, Sparkles, Loader2, ArrowRight, Check, X, Scale, ChevronRight } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { SEO } from "@/components/common/SEO";
import SmartImage from "@/components/common/SmartImage";

const suggestions = [
  { id: "s1", text: "Best 7-seater under ₹25 lakh in India" },
  { id: "s2", text: "Tata Nexon EV vs MG ZS EV — which is better?" },
  { id: "s3", text: "Is a hybrid worth it if I drive 40 km/day in Bengaluru?" },
  { id: "s4", text: "Sedans with ADAS under ₹20 lakh" },
];

function CarThumb({ car }) {
  return (
    <Link to={`/cars/${car.slug}`} data-testid={`chat-card-${car.slug}`}
      className="group rounded-2xl border border-border bg-card overflow-hidden hover:border-blue-400 hover:shadow-md transition-all flex flex-col">
      <div className="aspect-[16/10] overflow-hidden bg-secondary">
        <SmartImage src={car.image} alt={`${car.brand_name} ${car.model}`} label={`${car.brand_name} ${car.model}`}
          className="group-hover:scale-[1.04] transition-transform duration-500"/>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{car.brand_name} · {car.body_type}</div>
        <div className="font-display text-lg mt-1 tracking-tight">{car.model}</div>
        <div className="mt-2 font-mono text-sm">{formatINR(car.price_min)}{car.price_max > car.price_min ? ` – ${formatINR(car.price_max)}` : ""}</div>
        <div className="mt-3 flex gap-2 text-[11px] text-muted-foreground">
          {car.specs?.seats && <span>· {car.specs.seats} seats</span>}
          {car.fuel_types?.[0] && <span>· {car.fuel_types[0]}</span>}
          {car.specs?.mileage_kmpl && <span>· {car.specs.mileage_kmpl} kmpl</span>}
        </div>
        <div className="mt-auto pt-4 flex gap-2">
          <span className="flex-1 h-9 rounded-full bg-[#2563EB] text-white text-xs font-medium grid place-items-center">View details</span>
          <Link to={`/compare?slugs=${car.slug}`} onClick={(e) => e.stopPropagation()}
            className="h-9 px-3 rounded-full border border-border text-xs font-medium inline-flex items-center gap-1 hover:bg-secondary">
            <Scale className="w-3 h-3"/>
          </Link>
        </div>
      </div>
    </Link>
  );
}

function AssistantBubble({ structured, cards, onFollowUp }) {
  if (!structured) return null;
  const { headline, summary, key_points = [], pros = [], cons = [], follow_ups = [] } = structured;
  return (
    <div className="w-full max-w-full space-y-4" data-testid="chat-structured-reply">
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="text-[11px] uppercase tracking-[0.2em] text-ai font-semibold mb-2 flex items-center gap-1">
          <Sparkles className="w-3 h-3"/> AI Expert
        </div>
        {headline && <div className="font-display text-lg tracking-tight leading-snug">{headline}</div>}
        {summary && <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{summary}</p>}
        {key_points.length > 0 && (
          <ul className="mt-4 space-y-2">
            {key_points.map((k) => (
              <li key={k} className="flex gap-2 text-sm"><ChevronRight className="w-4 h-4 text-blue-500 mt-0.5 shrink-0"/><span>{k}</span></li>
            ))}
          </ul>
        )}
      </div>

      {cards && cards.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Recommended for you</div>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="chat-car-cards">
            {cards.map((c) => <CarThumb key={c.slug} car={c}/>)}
          </div>
        </div>
      )}

      {(pros.length > 0 || cons.length > 0) && (
        <div className="grid sm:grid-cols-2 gap-3">
          {pros.length > 0 && (
            <div className="rounded-2xl border border-border bg-emerald-50/40 dark:bg-emerald-950/20 p-4">
              <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-400 font-semibold mb-2">Pros</div>
              <ul className="space-y-2">
                {pros.map((p) => <li key={p} className="flex gap-2 text-sm"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0"/>{p}</li>)}
              </ul>
            </div>
          )}
          {cons.length > 0 && (
            <div className="rounded-2xl border border-border bg-rose-50/40 dark:bg-rose-950/20 p-4">
              <div className="text-[11px] uppercase tracking-[0.2em] text-rose-700 dark:text-rose-400 font-semibold mb-2">Cons</div>
              <ul className="space-y-2">
                {cons.map((p) => <li key={p} className="flex gap-2 text-sm"><X className="w-4 h-4 text-rose-600 mt-0.5 shrink-0"/>{p}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {follow_ups.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Ask next</div>
          <div className="flex flex-wrap gap-2">
            {follow_ups.map((f) => (
              <button key={f} data-testid={`follow-up-${f.slice(0,20)}`} onClick={() => onFollowUp(f)}
                className="text-xs px-3 py-2 rounded-full border border-border bg-card hover:bg-secondary inline-flex items-center gap-1">
                {f} <ArrowRight className="w-3 h-3"/>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Chat() {
  const [params] = useSearchParams();
  const initialQ = params.get("q") || "";
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // {id, role, content (user), structured, cards, pending}
  const [input, setInput] = useState(initialQ);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg) return;
    setInput("");
    const uid = `u-${Date.now()}`;
    const aid = `a-${Date.now()}`;
    const optimistic = [...messages, { id: uid, role: "user", content: msg }, { id: aid, role: "assistant", pending: true }];
    setMessages(optimistic);
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat/structured", { session_id: sessionId, message: msg });
      setSessionId(data.session_id);
      setMessages((prev) => prev.map((m) => m.id === aid ? { ...m, pending: false, structured: data.structured, cards: data.cards } : m));
    } catch {
      setMessages((prev) => prev.map((m) => m.id === aid ? { ...m, pending: false,
        structured: { headline: "AI is temporarily unavailable.", summary: "Please try again in a moment." } } : m));
    } finally {
      setSending(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }
  };

  useEffect(() => { if (initialQ) send(initialQ); }, []);

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10 min-h-[80vh] flex flex-col" data-testid="chat-page">
      <SEO title="AI Chat Assistant" description="Ask anything about cars in India — recommendations, comparisons, ownership cost, EVs and more."/>
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Ask Expert</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-2 tracking-tighter">Ask anything about cars.</h1>
      <p className="text-muted-foreground mt-3 max-w-xl">Recommendations, comparisons, EV viability, EMI, insurance — all with visual answers you can act on.</p>

      <div className="mt-8 flex-1 rounded-3xl border border-border bg-card/40 p-4 sm:p-6 overflow-y-auto min-h-[420px]" data-testid="chat-history">
        {messages.length === 0 && (
          <div>
            <div className="text-sm text-muted-foreground mb-3">Try one of these:</div>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((s) => (
                <button key={s.id} data-testid={`chat-suggestion-${s.id}`} onClick={() => send(s.text)}
                  className="text-sm px-3 py-2 rounded-full border border-border bg-card hover:bg-secondary">
                  {s.text}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="space-y-6">
          {messages.map((m) => (
            <div key={m.id} data-testid={`chat-msg-${m.role}-${m.id}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "user" ? (
                <div className="max-w-[85%] rounded-2xl px-4 py-3 whitespace-pre-wrap text-sm leading-relaxed bg-[#2563EB] text-white">
                  {m.content}
                </div>
              ) : (
                <div className="w-full">
                  {m.pending ? (
                    <div className="rounded-2xl border border-border bg-card p-5 inline-flex items-center gap-2 text-muted-foreground text-sm">
                      <Loader2 className="w-4 h-4 animate-spin"/> Thinking about your options…
                    </div>
                  ) : (
                    <AssistantBubble structured={m.structured} cards={m.cards} onFollowUp={send}/>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        <div ref={bottomRef}/>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4">
        <div className="rounded-2xl border border-border bg-card p-2 flex items-center gap-2 shadow-sm">
          <Sparkles className="w-4 h-4 text-ai ml-2 shrink-0"/>
          <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about any car in India…" className="flex-1 bg-transparent outline-none py-2 text-sm"/>
          <button data-testid="chat-send" type="submit" disabled={sending || !input.trim()}
            className="h-10 px-4 rounded-xl bg-gradient-ai text-white text-sm inline-flex items-center gap-2 disabled:opacity-50">
            {sending ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Send className="w-4 h-4"/> Ask</>}
          </button>
        </div>
      </form>
    </div>
  );
}
